import { Injectable, signal } from '@angular/core';
import { type DashboardLayoutCardItem } from '../../shared/components/dashboard/dashboard-widget-picker-dialog.component';

export interface SectionDef {
  id: string;
  title: string;
}

const LS_PREFIX = 'prism_section_layout_';

/**
 * Lightweight, localStorage-backed service for managing per-page section visibility.
 *
 * Usage in a component:
 * ```ts
 * private sectionLayout = inject(DashboardSectionLayoutService);
 *
 * readonly PAGE_KEY = 'my-page';
 * readonly SECTIONS: SectionDef[] = [
 *   { id: 'kpi_cards', title: 'KPI Cards' },
 *   { id: 'trend_chart', title: 'Trend Chart' },
 * ];
 *
 * ngOnInit() {
 *   this.sectionLayout.init(this.PAGE_KEY, this.SECTIONS);
 * }
 *
 * isVisible(id: string): boolean {
 *   return this.sectionLayout.isVisible(this.PAGE_KEY, id);
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class DashboardSectionLayoutService {
  /**
   * Internal reactive store: pageKey → (sectionId → visible).
   * Using a plain signal for simplicity; each write replaces the whole object.
   */
  private readonly _state = signal<Record<string, Record<string, boolean>>>({});

  /**
   * Initialize (or restore from localStorage) the visibility state for a page.
   * Safe to call multiple times — already-initialised pages are skipped.
   */
  init(pageKey: string, sections: SectionDef[]): void {
    const current = this._state();
    if (current[pageKey]) return; // already initialised

    const stored = this._loadFromStorage(pageKey);
    const visibility: Record<string, boolean> = {};

    for (const s of sections) {
      // Default: visible=true unless localStorage says otherwise
      visibility[s.id] = stored?.[s.id] !== false;
    }

    this._state.update((prev) => ({ ...prev, [pageKey]: visibility }));
  }

  /** Returns true if the section should be rendered. Defaults to true if unknown. */
  isVisible(pageKey: string, sectionId: string): boolean {
    return this._state()[pageKey]?.[sectionId] !== false;
  }

  /**
   * Build the cards array expected by DashboardWidgetPickerDialogComponent
   * for the `layout` mode, reflecting current visibility state.
   */
  getSectionsForDialog(pageKey: string, sections: SectionDef[]): DashboardLayoutCardItem[] {
    return sections.map((s) => ({
      key: s.id,
      title: s.title,
      selected: this.isVisible(pageKey, s.id),
    }));
  }

  /**
   * Apply the dialog result: `selectedKeys` contains the IDs of *visible* sections.
   * Sections not in the list are hidden.
   */
  applyVisibility(pageKey: string, sections: SectionDef[], selectedKeys: string[]): void {
    const selected = new Set(selectedKeys);
    const visibility: Record<string, boolean> = {};

    for (const s of sections) {
      visibility[s.id] = selected.has(s.id);
    }

    this._state.update((prev) => ({ ...prev, [pageKey]: visibility }));
    this._saveToStorage(pageKey, visibility);
  }

  /** Reset all sections to visible for a page. */
  reset(pageKey: string, sections: SectionDef[]): void {
    const visibility: Record<string, boolean> = {};
    for (const s of sections) {
      visibility[s.id] = true;
    }
    this._state.update((prev) => ({ ...prev, [pageKey]: visibility }));
    this._saveToStorage(pageKey, visibility);
  }

  // ─── Private helpers ─────────────────────────────────────────────────────

  private _loadFromStorage(pageKey: string): Record<string, boolean> | null {
    try {
      const raw = localStorage.getItem(`${LS_PREFIX}${pageKey}`);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private _saveToStorage(pageKey: string, visibility: Record<string, boolean>): void {
    try {
      localStorage.setItem(`${LS_PREFIX}${pageKey}`, JSON.stringify(visibility));
    } catch {
      // localStorage unavailable (e.g., private mode) — silently ignore
    }
  }
}
